import React from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import Clipboard from "@react-native-clipboard/clipboard";
import { NormalText, PrimaryButton } from "../../../common/native";
import { ADMIN_TOKENS } from "../../../../styles/adminTokens";

type DeployHelpWidgetProps = {
  onShowTips: () => void;
};

type CopiedContact = "email" | "phone" | null;

const SUPPORT_EMAIL = "support@clinicos.ai";
const SUPPORT_PHONE = "+1 (909) 359-2587";

const DeployHelpWidget: React.FC<DeployHelpWidgetProps> = ({ onShowTips }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [copiedContact, setCopiedContact] = React.useState<CopiedContact>(null);

  const copyContact = React.useCallback(
    (type: Exclude<CopiedContact, null>, value: string) => {
      Clipboard.setString(value);
      setCopiedContact(type);
      setTimeout(() => setCopiedContact(null), 1500);
    },
    [],
  );

  const handleShowTips = React.useCallback(() => {
    setIsOpen(false);
    onShowTips();
  }, [onShowTips]);

  return (
    <View pointerEvents="box-none" style={styles.root}>
      {isOpen ? (
        <View style={styles.panel}>
          <View style={styles.panelHeader}>
            <View style={styles.titleRow}>
              <View style={styles.headerIconBadge}>
                <MaterialIcons
                  name="help-outline"
                  size={18}
                  color={ADMIN_TOKENS.color.accentDark}
                />
              </View>
              <NormalText
                text="Need help installing it?"
                fontWeight="600"
                style={styles.titleText}
              />
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close deploy help"
              onPress={() => setIsOpen(false)}
              style={({ pressed }) => [
                styles.closeButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <MaterialIcons
                name="close"
                size={20}
                color={ADMIN_TOKENS.color.textSecondary}
              />
            </Pressable>
          </View>
          <Text style={styles.bodyText}>
            If setup gets stuck, contact us and we can help you get the widget
            live.
          </Text>
          <View style={styles.contactActions}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Copy Clinicos support email"
              onPress={() => copyContact("email", SUPPORT_EMAIL)}
              style={({ pressed }) => [
                styles.contactButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <MaterialIcons
                name="mail-outline"
                size={18}
                color={ADMIN_TOKENS.color.accentDark}
              />
              <NormalText
                text={
                  copiedContact === "email"
                    ? "Copied to clipboard"
                    : SUPPORT_EMAIL
                }
                style={styles.contactText}
              />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Copy Clinicos support phone number"
              onPress={() => copyContact("phone", SUPPORT_PHONE)}
              style={({ pressed }) => [
                styles.contactButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <MaterialIcons
                name="phone"
                size={18}
                color={ADMIN_TOKENS.color.accentDark}
              />
              <NormalText
                text={
                  copiedContact === "phone"
                    ? "Copied to clipboard"
                    : SUPPORT_PHONE
                }
                style={styles.contactText}
              />
            </Pressable>
          </View>
          <PrimaryButton
            text="Show tips"
            onPress={handleShowTips}
            fullWidth
            icon={
              <MaterialIcons
                name="info-outline"
                size={18}
                color={ADMIN_TOKENS.color.surface}
              />
            }
            style={styles.tipsButton}
          />
        </View>
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={
          isOpen ? "Close deploy help" : "Open deploy installation help"
        }
        accessibilityState={{ expanded: isOpen }}
        onPress={() => setIsOpen((current) => !current)}
        style={({ pressed }) => [
          styles.fab,
          pressed ? styles.fabPressed : null,
        ]}
      >
        <MaterialIcons
          name={isOpen ? "close" : "help-outline"}
          size={26}
          color={ADMIN_TOKENS.color.surface}
        />
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    position: "absolute",
    left: ADMIN_TOKENS.spacing.xl,
    right: ADMIN_TOKENS.spacing.xl,
    bottom: ADMIN_TOKENS.spacing.xxl,
    alignItems: "flex-end",
    gap: ADMIN_TOKENS.spacing.md,
    zIndex: ADMIN_TOKENS.zIndex.above,
  },
  panel: {
    width: "100%",
    maxWidth: ADMIN_TOKENS.size.eventCardMaxWidth,
    backgroundColor: ADMIN_TOKENS.color.surface,
    borderRadius: ADMIN_TOKENS.radius.xl,
    borderWidth: ADMIN_TOKENS.borderWidth.sm,
    borderColor: ADMIN_TOKENS.color.border,
    padding: ADMIN_TOKENS.spacing.lg,
    gap: ADMIN_TOKENS.spacing.md,
    ...ADMIN_TOKENS.shadow.card,
  },
  panelHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: ADMIN_TOKENS.spacing.md,
  },
  titleRow: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: ADMIN_TOKENS.spacing.sm,
  },
  headerIconBadge: {
    width: 32,
    height: 32,
    borderRadius: ADMIN_TOKENS.radius.xl,
    backgroundColor: ADMIN_TOKENS.color.surfaceAccent,
    alignItems: "center",
    justifyContent: "center",
  },
  titleText: {
    flex: 1,
    minWidth: 0,
    fontSize: ADMIN_TOKENS.fontSize.md,
    lineHeight: ADMIN_TOKENS.lineHeight.md,
    color: ADMIN_TOKENS.color.textPrimary,
  },
  closeButton: {
    width: 44,
    height: 44,
    borderRadius: ADMIN_TOKENS.radius.xxl,
    alignItems: "center",
    justifyContent: "center",
  },
  bodyText: {
    fontFamily: ADMIN_TOKENS.font.medium,
    fontSize: ADMIN_TOKENS.fontSize.sm,
    lineHeight: ADMIN_TOKENS.lineHeight.sm,
    color: ADMIN_TOKENS.color.textSecondary,
  },
  contactActions: {
    gap: ADMIN_TOKENS.spacing.sm,
  },
  contactButton: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: ADMIN_TOKENS.spacing.sm,
    paddingHorizontal: ADMIN_TOKENS.spacing.md,
    borderRadius: ADMIN_TOKENS.radius.lg,
    borderWidth: ADMIN_TOKENS.borderWidth.sm,
    borderColor: ADMIN_TOKENS.color.borderAccent,
    backgroundColor: ADMIN_TOKENS.color.surfaceAccent,
  },
  contactText: {
    flex: 1,
    minWidth: 0,
    fontSize: ADMIN_TOKENS.fontSize.sm,
    lineHeight: ADMIN_TOKENS.lineHeight.sm,
    color: ADMIN_TOKENS.color.accentDark,
  },
  tipsButton: {
    minHeight: 46,
  },
  fab: {
    width: 56,
    height: 56,
    borderRadius: ADMIN_TOKENS.radius.xxl,
    backgroundColor: ADMIN_TOKENS.color.accent,
    alignItems: "center",
    justifyContent: "center",
    ...ADMIN_TOKENS.shadow.card,
  },
  fabPressed: {
    opacity: ADMIN_TOKENS.opacity.muted,
  },
  pressed: {
    opacity: ADMIN_TOKENS.opacity.muted,
  },
});

export default DeployHelpWidget;
